// Brand configuration. EVERYTHING brand-specific is here so the brand
// can be decided later without touching components. Each field falls
// back to a sensible neutral default and can be overridden at build
// time via a VITE_BRAND_* env var — no code change needed to rebrand.

function envStr(key: string, fallback: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[key];
  return v && v.length > 0 ? v : fallback;
}

export interface Brand {
  /** Company / org name. */
  name: string;
  /** Product name shown in the UI. */
  productName: string;
  /** One-line positioning statement shown on the sign-in landing. */
  tagline: string;
  /** Primary brand colour seed; injected into --brand-primary. */
  primary: string;
  primaryHover: string;
  /** Accent colour seed; injected into --brand-accent. */
  accent: string;
  /** Optional font family override (must be web-available). */
  fontSans: string | null;
  supportEmail: string;
  /** Control-plane Worker base URL. The MCP endpoint shown in the UI is
   * NOT configured here — it is read at runtime from the worker's RFC
   * 9728 metadata (lib/useMcpUrl.ts) so it can't drift from
   * MCP_RESOURCE_URL. */
  apiBaseUrl: string;
}

export const brand: Brand = {
  name: envStr("VITE_BRAND_NAME", "plaingraph"),
  productName: envStr("VITE_BRAND_PRODUCT", "plaingraph"),
  tagline: envStr(
    "VITE_BRAND_TAGLINE",
    "Agent-native company intelligence and sanctions screening.",
  ),
  primary: envStr("VITE_BRAND_PRIMARY", "#2F47DA"),
  primaryHover: envStr("VITE_BRAND_PRIMARY_HOVER", "#2638B0"),
  accent: envStr("VITE_BRAND_ACCENT", "#3D5AF1"),
  fontSans: envStr("VITE_BRAND_FONT", "") || null,
  supportEmail: envStr("VITE_BRAND_SUPPORT_EMAIL", "support@plaingraph.com"),
  apiBaseUrl: envStr("VITE_API_BASE_URL", "http://localhost:8787"),
};

// Pushes the brand seed into the CSS custom properties defined in
// tokens.css. Called once at startup so env-driven rebrands need no
// CSS rebuild.
export function applyBrand(b: Brand = brand): void {
  const r = document.documentElement;
  r.style.setProperty("--brand-primary", b.primary);
  r.style.setProperty("--brand-primary-hover", b.primaryHover);
  r.style.setProperty("--brand-accent", b.accent);
  if (b.fontSans) r.style.setProperty("--font-sans", b.fontSans);
  document.title = b.productName;
}
